/**
 * Mobile Analytics — lightweight client-side event tracking.
 *
 * Tracks screen views, feature usage, and user properties.
 * Batches events and sends to server every 30 seconds.
 * Falls back to local storage if offline.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { getClient } from '../api/client';

const STORAGE_KEY = 'mobile-analytics-queue';
const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE_SIZE = 200;

interface AnalyticsEvent {
  type: 'screen_view' | 'feature_use' | 'funnel_step' | 'error';
  name: string;
  properties?: Record<string, string | number | boolean>;
  timestamp: string;
}

let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function enqueue(event: AnalyticsEvent): void {
  queue.push(event);
  if (queue.length > MAX_QUEUE_SIZE) queue.shift();
}

/** Track a screen view. */
export function trackScreen(screenName: string): void {
  enqueue({
    type: 'screen_view',
    name: screenName,
    timestamp: new Date().toISOString(),
  });
}

/** Track feature usage (e.g., 'reminder_created', 'snooze', 'calendar_view_change'). */
export function trackFeature(name: string, properties?: Record<string, string | number | boolean>): void {
  enqueue({
    type: 'feature_use',
    name,
    properties,
    timestamp: new Date().toISOString(),
  });
}

/** Track funnel step (auth flow, onboarding). */
export function trackFunnel(step: string, properties?: Record<string, string | number | boolean>): void {
  enqueue({
    type: 'funnel_step',
    name: step,
    properties,
    timestamp: new Date().toISOString(),
  });
}

/** Track a client-side error. */
export function trackError(name: string, detail: string): void {
  enqueue({
    type: 'error',
    name,
    properties: { detail },
    timestamp: new Date().toISOString(),
  });
}

/** Set user properties (language, timezone, platform, app version, tier). */
export function setUserProperties(props: Record<string, string | number | boolean>): void {
  enqueue({
    type: 'feature_use',
    name: 'user_properties',
    properties: { ...props, platform: Platform.OS },
    timestamp: new Date().toISOString(),
  });
}

/** Flush queued events to server. */
async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = [...queue];
  queue = [];
  try {
    const client = getClient();
    await client.post('/api/v1/analytics/events', { events: batch });
  } catch {
    // Re-queue on failure and persist
    queue = [...batch, ...queue].slice(0, MAX_QUEUE_SIZE);
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch { /* ignore */ }
  }
}

/** Initialize analytics — restore queue from storage, start flush timer. */
export async function initAnalytics(): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) queue = parsed.slice(-MAX_QUEUE_SIZE);
      await AsyncStorage.removeItem(STORAGE_KEY);
    }
  } catch { /* ignore */ }

  if (!flushTimer) {
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  }
}
